import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';

// Define the structure for reservation history entries
export interface ReservationHistoryEntry {
  id?: string;
  reservationId: string;
  action: 'created' | 'updated' | 'cancelled' | 'rescheduled' | 'checked_in' | 'checked_out' | 'payment_processed' | 'payment_refunded' | 'status_changed';
  timestamp: Date;
  staffId?: string;
  staffName?: string;
  userId?: string;
  userName?: string;
  channel: 'staff' | 'customer' | 'admin' | 'system';
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
    displayName?: string;
  }[];
  details?: {
    [key: string]: any;
  };
  notes?: string;
  ipAddress?: string;
  userAgent?: string;
}

// Reference to the reservation history collection
const historyRef = collection(db, 'reservationHistory');

// Function to add a history entry
export const addReservationHistoryEntry = async (entry: Omit<ReservationHistoryEntry, 'id'>): Promise<void> => {
  try {
    // Filter out undefined values as Firestore doesn't accept them
    const cleanedEntry = Object.fromEntries(
      Object.entries({
        ...entry,
        timestamp: Timestamp.fromDate(entry.timestamp)
      }).filter(([_, value]) => value !== undefined)
    );
    
    await addDoc(historyRef, cleanedEntry);
  } catch (error) {
    console.error('Error adding reservation history entry:', error);
    throw error;
  }
};

// Function to get all history entries for a reservation
export const getReservationHistory = async (reservationId: string): Promise<ReservationHistoryEntry[]> => {
  try {
    const q = query(
      historyRef,
      where('reservationId', '==', reservationId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp.toDate()
    } as ReservationHistoryEntry));
  } catch (error) {
    console.error('Error fetching reservation history:', error);
    throw error;
  }
};

// Function to track reservation creation
export const trackReservationCreation = async (
  reservationId: string,
  reservationData: any,
  createdBy: string,
  channel: 'staff' | 'customer' | 'admin' | 'system',
  userProfile?: { firstName?: string; lastName?: string; email?: string }
): Promise<void> => {
  try {
    // Get staff/user information
    let staffName = '';
    let userId = '';
    let userName = '';
    
    if (channel === 'staff' && userProfile) {
      // Use the provided user profile information
      staffName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
    }
    
    const historyEntry: any = {
      reservationId,
      action: 'created',
      timestamp: new Date(),
      channel,
      details: {
        vesselType: reservationData.vesselType,
        date: reservationData.date,
        startTime: reservationData.startTime,
        endTime: reservationData.endTime,
        customerName: `${reservationData.customerDetails?.firstName} ${reservationData.customerDetails?.lastName}`,
        customerEmail: reservationData.customerDetails?.email,
        partySize: reservationData.partySize,
        totalPrice: reservationData.finalPrice || reservationData.totalPrice
      }
    };
    
    // Only add fields if they have values
    if (channel === 'staff' && createdBy) {
      historyEntry.staffId = createdBy;
      if (staffName) historyEntry.staffName = staffName;
    } else if (channel === 'customer' && createdBy) {
      historyEntry.userId = createdBy;
      if (userName) historyEntry.userName = userName;
    }
    
    await addReservationHistoryEntry(historyEntry);
  } catch (error) {
    console.error('Error tracking reservation creation:', error);
    // Don't throw error to avoid breaking reservation creation
  }
};

// Function to track reservation updates/rescheduling
export const trackReservationUpdate = async (
  reservationId: string,
  oldData: any,
  newData: any,
  updatedBy: string,
  channel: 'staff' | 'customer' | 'admin' | 'system',
  notes?: string,
  userProfile?: { firstName?: string; lastName?: string; email?: string }
): Promise<void> => {
  try {
    const changes = [];
    
    // Compare key fields and track changes
    const fieldsToTrack = {
      date: 'Date',
      startTime: 'Start Time',
      endTime: 'End Time',
      vesselType: 'Vessel Type',
      vesselId: 'Vessel',
      partySize: 'Party Size',
      status: 'Status',
      notes: 'Notes'
    };
    
    for (const [field, displayName] of Object.entries(fieldsToTrack)) {
      if (oldData[field] !== newData[field]) {
        changes.push({
          field,
          displayName,
          oldValue: oldData[field],
          newValue: newData[field]
        });
      }
    }
    
    // Check for customer details changes
    if (oldData.customerDetails && newData.customerDetails) {
      const customerFields = {
        firstName: 'Customer First Name',
        lastName: 'Customer Last Name',
        email: 'Customer Email',
        phone: 'Customer Phone'
      };
      
      for (const [field, displayName] of Object.entries(customerFields)) {
        if (oldData.customerDetails[field] !== newData.customerDetails[field]) {
          changes.push({
            field: `customerDetails.${field}`,
            displayName,
            oldValue: oldData.customerDetails[field],
            newValue: newData.customerDetails[field]
          });
        }
      }
    }
    
    // Determine the action type based on changes
    let action: ReservationHistoryEntry['action'] = 'updated';
    if (changes.some(change => ['date', 'startTime', 'endTime'].includes(change.field))) {
      action = 'rescheduled';
    }
    if (changes.some(change => change.field === 'status')) {
      action = 'status_changed';
    }
    
    // Get staff/user information
    let staffName = '';
    let userId = '';
    let userName = '';
    
    if (channel === 'staff' && userProfile) {
      // Use the provided user profile information
      staffName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
    }
    
    const historyEntry: any = {
      reservationId,
      action,
      timestamp: new Date(),
      channel,
      changes
    };
    
    // Only add fields if they have values
    if (channel === 'staff' && updatedBy) {
      historyEntry.staffId = updatedBy;
      if (staffName) historyEntry.staffName = staffName;
    } else if (channel === 'customer' && updatedBy) {
      historyEntry.userId = updatedBy;
      if (userName) historyEntry.userName = userName;
    }
    
    if (notes) {
      historyEntry.notes = notes;
    }
    
    await addReservationHistoryEntry(historyEntry);
  } catch (error) {
    console.error('Error tracking reservation update:', error);
    // Don't throw error to avoid breaking reservation update
  }
};

// Function to track specific actions (check-in, check-out, cancellation, etc.)
export const trackReservationAction = async (
  reservationId: string,
  action: 'checked_in' | 'checked_out' | 'cancelled' | 'payment_processed' | 'payment_refunded',
  actionBy: string,
  channel: 'staff' | 'customer' | 'admin' | 'system',
  details?: { [key: string]: any },
  notes?: string,
  userProfile?: { firstName?: string; lastName?: string; email?: string }
): Promise<void> => {
  try {
    // Get staff/user information
    let staffName = '';
    let userId = '';
    let userName = '';
    
    if (channel === 'staff' && userProfile) {
      // Use the provided user profile information
      staffName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
    }
    
    const historyEntry: any = {
      reservationId,
      action,
      timestamp: new Date(),
      channel
    };
    
    // Only add fields if they have values
    if (channel === 'staff' && actionBy) {
      historyEntry.staffId = actionBy;
      if (staffName) historyEntry.staffName = staffName;
    } else if (channel === 'customer' && actionBy) {
      historyEntry.userId = actionBy;
      if (userName) historyEntry.userName = userName;
    }
    
    if (details) {
      historyEntry.details = details;
    }
    
    if (notes) {
      historyEntry.notes = notes;
    }
    
    await addReservationHistoryEntry(historyEntry);
  } catch (error) {
    console.error('Error tracking reservation action:', error);
    // Don't throw error to avoid breaking the main action
  }
};

// Helper function to format history entries for display
export const formatHistoryEntry = (entry: ReservationHistoryEntry): string => {
  const actor = entry.staffName || entry.userName || 'System';
  const timestamp = entry.timestamp.toLocaleDateString() + ' at ' + entry.timestamp.toLocaleTimeString();
  
  switch (entry.action) {
    case 'created':
      return `${actor} created the reservation via ${entry.channel}`;
    case 'rescheduled':
      return `${actor} rescheduled the reservation`;
    case 'updated':
      return `${actor} updated the reservation`;
    case 'cancelled':
      return `${actor} cancelled the reservation`;
    case 'checked_in':
      return `${actor} checked in the customer`;
    case 'checked_out':
      return `${actor} checked out the customer`;
    case 'payment_processed':
      return `${actor} processed payment`;
    case 'payment_refunded':
      return `${actor} processed refund`;
    case 'status_changed':
      return `${actor} changed the reservation status`;
    default:
      return `${actor} performed an action on the reservation`;
  }
}; 